import * as Types from '@aeppic/types'
import type { EditableDocument } from '../model/editable-document.js'
import { getLogicalParentElement } from './overlay-host.js'

const EDIT_CONTEXT_DOM_KEY = '__aeEditContext'
const EDIT_CONTEXT_BOUNDARY_ATTRIBUTE = 'data-ae-edit-context-boundary'

/**
 * Check whether an element is rendered inside an edit-context boundary.
 * The element itself is included so context-scoped Aeppic instances can use
 * their dynamic component root as the lookup point.
 */
export function isInsideEditContextBoundary(el: HTMLElement): boolean {
  let current = el

  while (current) {
    if (current.hasAttribute?.(EDIT_CONTEXT_BOUNDARY_ATTRIBUTE)) {
      return true
    }
    current = getLogicalParentElement(current)
  }

  return false
}

/**
 * Walk DOM ancestors to find the nearest EditContext. Layered overlay surfaces
 * continue from the element that opened them.
 * Returns null if none found.
 */
export function findEditContextInDOM(el: HTMLElement, includeElement = false): EditContext | null {
  let current = includeElement ? el : el && getLogicalParentElement(el)

  while (current) {
    if (current.hasAttribute?.(EDIT_CONTEXT_BOUNDARY_ATTRIBUTE)) {
      return null
    }

    const ctx = (current as any)[EDIT_CONTEXT_DOM_KEY]
    if (ctx && ctx instanceof EditContext) {
      return ctx
    }
    current = getLogicalParentElement(current)
  }

  return null
}

/**
 * Walk DOM ancestors (following layered overlay owners) to the element an
 * EditContext is mounted on, ignoring boundaries. Used for diagnostics.
 */
export function findEditContextOwnerElement(el: HTMLElement): HTMLElement | null {
  let current = el && getLogicalParentElement(el)

  while (current) {
    if ((current as any)[EDIT_CONTEXT_DOM_KEY]) {
      return current
    }
    current = getLogicalParentElement(current)
  }

  return null
}

export interface EditContextTrackOptions {
  newDocument?: boolean
  mode?: 'autosave' | 'implicit' | 'explicit'
  autoSave?: boolean
  autoSaveDelay?: number
  trackDescendants?: boolean
  implicit?: boolean
  explicit?: boolean
}

export interface TrackedEntry {
  document: EditableDocument
  options: EditContextTrackOptions
}

export interface EditContextDocumentTreeChangeSummary {
  added: number
  changed: number
  removed: number
  hasChanges: boolean
}

export interface EditContextCreateOptions extends EditContextTrackOptions {
  debugName?: string
}

type EditContextState = 'idle' | 'editing' | 'saving'
type SaveChangesConfirmation = (documents: EditableDocument[]) => boolean
type FlushCallback = (documents: EditableDocument[], deletedDocuments: Types.Document[]) => void | Promise<void>

function logEditContext(...args: any[]) {
  if ((globalThis as any).__AE_EDIT_CONTEXT_DEBUG__) {
    console.debug(...args)
  }
}

// Autosave must never interrupt active data entry. Forms use this as the
// inactivity fallback; leaving a field can still save immediately.
const DEFAULT_AUTOSAVE_DELAY = 60000

function documentBelongsToTree(document: Pick<Types.Document, 'id' | 'p' | 'a'>, rootDocumentId: string): boolean {
  if (!rootDocumentId) {
    return false
  }

  if (document.id === rootDocumentId || document.p === rootDocumentId) {
    return true
  }

  return Array.isArray(document.a) && document.a.indexOf(rootDocumentId) >= 0
}

function emptyDocumentTreeChangeSummary(): EditContextDocumentTreeChangeSummary {
  return {
    added: 0,
    changed: 0,
    removed: 0,
    hasChanges: false,
  }
}

/**
 * EditContext tracks one or more EditableDocuments, manages
 * their save lifecycle (auto/explicit/implicit), and exposes
 * reactive state for UI binding.
 *
 * Phase 1: single-context, no DOM hierarchy lookup.
 */
export class EditContext {
  private _tracked: Map<string, TrackedEntry> = new Map()
  private _debugName = 'unnamed'
  private _state: EditContextState = 'idle'
  private _dirty = false
  private _lastSavedAt: Date | null = null
  private _autoSaveTimer: ReturnType<typeof setTimeout> | null = null
  private _pendingSave: Promise<void> | null = null
  private _autoSaveDelay: number = DEFAULT_AUTOSAVE_DELAY
  private _autoSave = false
  private _implicit = false
  private _explicit = false
  private _trackDescendants = true
  private _rootDeletionPending = false
  private _destroyed = false
  private _listeners: Set<() => void> = new Set()
  private _deletedDocuments: Map<string, Types.Document> = new Map()
  private _flushCallback: FlushCallback | null = null
  private _discardCallback: ((documents: EditableDocument[]) => void) | null = null
  private _saveChangesConfirmation: SaveChangesConfirmation | null = null
  private _configured = false

  configure(options: EditContextCreateOptions = {}) {
    if (this._configured || this._tracked.size > 0) {
      return
    }

    this._configured = true

    const mode = options.mode
    this._autoSave = mode ? mode === 'autosave' : options.autoSave !== false
    this._implicit = mode ? mode === 'implicit' : options.implicit === true
    this._explicit = mode ? mode === 'explicit' : options.explicit === true
    this._autoSaveDelay = options.autoSaveDelay ?? DEFAULT_AUTOSAVE_DELAY
    this._trackDescendants = options.trackDescendants !== false

    if (options.debugName) {
      this.setDebugName(options.debugName)
    }

    if (this._autoSave && !this._explicit && !this._implicit) {
      this._state = 'editing'
    }
  }

  get isEditing(): boolean {
    return this._state === 'editing' || this._state === 'saving'
  }

  get isSaving(): boolean {
    return this._state === 'saving'
  }

  get isDirty(): boolean {
    return this._dirty
  }

  get lastSavedAt(): Date | null {
    return this._lastSavedAt
  }

  get isAutoSave(): boolean {
    return this._autoSave
  }

  get isExplicit(): boolean {
    return this._explicit
  }

  get isImplicit(): boolean {
    return this._implicit
  }

  get trackDescendants(): boolean {
    return this._trackDescendants
  }

  get trackedDocumentIds(): string[] {
    return Array.from(this._tracked.keys())
  }

  get trackedDocuments(): EditableDocument[] {
    return Array.from(this._tracked.values()).map(entry => entry.document)
  }

  get deletedDocuments(): Types.Document[] {
    return Array.from(this._deletedDocuments.values())
  }

  getTrackedEntry(documentId: string): TrackedEntry | null {
    return this._tracked.get(documentId) || null
  }

  getDocumentTreeChangeSummary(documentId: string): EditContextDocumentTreeChangeSummary {
    const summary = emptyDocumentTreeChangeSummary()

    if (!documentId) {
      return summary
    }

    this._tracked.forEach(entry => {
      if (!documentBelongsToTree(entry.document, documentId)) {
        return
      }

      if (entry.document.v === 'initial') {
        summary.added += 1
      } else if (entry.document.isDirty) {
        summary.changed += 1
      }
    })

    this._deletedDocuments.forEach(document => {
      if (documentBelongsToTree(document, documentId)) {
        summary.removed += 1
      }
    })

    summary.hasChanges = summary.added > 0 || summary.changed > 0 || summary.removed > 0
    return summary
  }

  get isDestroyed(): boolean {
    return this._destroyed
  }

  setDebugName(name: string | null | undefined) {
    this._debugName = name || 'unnamed'
  }

  get debugName(): string {
    return this._debugName
  }

  debugSnapshot() {
    const rootDocumentId = this.trackedDocumentIds[0] || null
    const docs: { id: string, formId: string, text: string, dirty: boolean, dirtyFieldCount: number, root: boolean, dirtyFields: any[] }[] = []
    this._tracked.forEach((entry, id) => {
      const doc = entry.document as any
      const dirtyFields = typeof doc.getDirtyFields === 'function' ? doc.getDirtyFields() : []
      docs.push({
        id,
        formId: doc.f?.id || '',
        text: doc.__base?.data?.name || doc.__base?.data?.title || doc.__base?.data?.text || '',
        dirty: entry.document.isDirty,
        dirtyFieldCount: (entry.document as any).dirtyFieldCount ?? 0,
        root: id === rootDocumentId,
        dirtyFields,
      })
    })
    return {
      name: this._debugName,
      state: this._state,
      dirty: this._dirty,
      autoSave: this._autoSave,
      implicit: this._implicit,
      explicit: this._explicit,
      destroyed: this._destroyed,
      deletedDocumentIds: this.deletedDocuments.map(document => document.id),
      documents: docs,
    }
  }

  /**
   * Register a callback that performs the actual save (Aeppic.save).
   * Called by the Aeppic integration layer (Step 3).
   */
  setFlushCallback(callback: FlushCallback | null) {
    this._flushCallback = callback
  }

  setDiscardCallback(callback: ((documents: EditableDocument[]) => void) | null) {
    this._discardCallback = callback
  }

  /**
   * Register a callback used when dirty implicit/explicit documents are about
   * to be detached. Return true to save, false to discard.
   */
  setSaveChangesConfirmation(callback: SaveChangesConfirmation | null) {
    this._saveChangesConfirmation = callback
  }

  /**
   * Register a listener that fires whenever state changes.
   * Returns an unsubscribe function.
   */
  onChange(listener: () => void): () => void {
    this._listeners.add(listener)
    return () => { this._listeners.delete(listener) }
  }

  notifyDocumentChanged(documentId: string) {
    if (this._tracked.has(documentId)) {
      this._notify()
    }
  }

  markDeleted(document: Types.Document) {
    if (this._destroyed || !document?.id) {
      return
    }

    const trackedEntry = this._tracked.get(document.id)

    if (trackedEntry) {
      trackedEntry.document.setMutationCallback(null)
      this._tracked.delete(document.id)
    }

    this._deletedDocuments.set(document.id, document)
    this._dirty = true

    if ((this._implicit || this._explicit) && this._state === 'idle') {
      this._state = 'editing'
    }

    if (this._autoSave && this._state === 'editing') {
      this._scheduleAutoSave()
    }

    this._notify()
  }

  private _notify() {
    this._listeners.forEach(listener => {
      try { listener() } catch (e) { console.error('EditContext listener error', e) }
    })
  }

  /**
   * Track an EditableDocument in this context.
   */
  track(document: EditableDocument, options: EditContextTrackOptions = {}) {
    if (this._destroyed) {
      console.warn('EditContext: cannot track after destroy')
      return
    }

    const id = document.id
    if (this._tracked.has(id)) {
      logEditContext('[EditContext] already tracking document', {
        context: this._debugName,
        documentId: id,
        dirty: document.isDirty,
        trackedDocumentIds: this.trackedDocumentIds,
      })
      return
    }

    // Apply context-level options from first track call for callers that do
    // not configure the context through a dynamic component first.
    if (this._tracked.size === 0) {
      this.configure(options)
    }

    this._tracked.set(id, { document, options })

    logEditContext('[EditContext] track document', {
      context: this._debugName,
      documentId: id,
      dirty: document.isDirty,
      autoSave: this._autoSave,
      autoSaveDelay: this._autoSaveDelay,
      implicit: this._implicit,
      explicit: this._explicit,
      state: this._state,
      trackedDocumentIds: this.trackedDocumentIds,
      options,
    })

    // Hook mutation callback
    document.setMutationCallback(() => {
      logEditContext('[EditContext] editable document mutation callback', {
        context: this._debugName,
        documentId: id,
        dirty: document.isDirty,
        dirtyFieldCount: (document as any).dirtyFieldCount,
      })
      this._onDocumentMutated(id)
    })

    // Listeners learn about the newly tracked document even when it is clean.
    this._onDocumentMutated(id, { notify: true })
  }

  /**
   * Untrack a document (e.g. when its design is destroyed).
   */
  untrack(documentId: string, options: { resolveDirty?: boolean } = {}) {
    if (this._rootDeletionPending) {
      return
    }

    const entry = this._tracked.get(documentId)
    if (!entry) return

    // Descendant components can release their view while the owning context
    // remains alive. Only the owner should resolve dirty changes.
    if (options.resolveDirty !== false && entry.document.isDirty) {
      this._resolveDirtyDocumentsBeforeDetach()
    }

    entry.document.setMutationCallback(null)
    this._tracked.delete(documentId)
    this._recalculateDirty()
    this._notify()
  }

  /**
   * Start editing explicitly (for explicit mode).
   */
  startEditing() {
    if (this._destroyed || this._state === 'editing' || this._state === 'saving') {
      return
    }

    this._state = 'editing'
    this._notify()
  }

  /**
   * Save all tracked changes and exit editing mode.
   */
  stopEditing() {
    if (this._destroyed) return

    this._flush()
    this._state = 'idle'
    this._recalculateDirty()
    this._notify()
  }

  /**
   * Flush changes when the user leaves a form field. This is deliberately
   * separate from mutation handling so typing in a field never triggers a
   * save on its own.
   */
  saveOnFieldBlur() {
    if (this._destroyed || !this._autoSave || this._state !== 'editing' || !this._dirty) {
      return
    }

    this._cancelAutoSaveTimer()
    this._flush()
  }

  /**
   * Discard all changes and exit editing mode.
   * Reverts all tracked documents to their base state.
   */
  cancelEditing() {
    if (this._destroyed) return

    this._cancelAutoSaveTimer()

    // Revert all tracked documents to their baseline. The Aeppic integration
    // removes unpersisted documents from its committed-document cache.
    const documents = this.trackedDocuments
    this._tracked.forEach(entry => {
      if (entry.document.isDirty && entry.document.revertToBase) {
        entry.document.revertToBase()
      }
    })

    this._discardCallback?.(documents)
    this._deletedDocuments.clear()

    this._state = 'idle'
    this._dirty = false
    this._notify()
  }

  /** Resolve edits before their view is removed, while failures can still keep it open. */
  async saveBeforeNavigation(): Promise<void> {
    if (this._pendingSave) await this._pendingSave
    if (!this._dirty) return
    this._cancelAutoSaveTimer()
    const documents = this._getDirtyDocuments()
    const deletedDocuments = this.deletedDocuments

    this._state = 'saving'
    this._notify()

    try {
      if (!this._flushCallback) throw new Error('No edit-context save handler is available')
      await this._flushCallback(documents, deletedDocuments)
      if (documents.some(document => document.isDirty)) {
        throw new Error('Some changes could not be saved')
      }
      this._acknowledgeDeletions(deletedDocuments)
      this._markCreatedDocumentsSaved(documents)
      this.onSaveCompleted()
    } catch (error) {
      this._state = 'editing'
      this._recalculateDirty()
      this._notify()
      throw error
    }
  }

  /**
   * Called when Aeppic.save() completes for a tracked document.
   * Updates state and records the save timestamp.
   */
  onSaveCompleted() {
    this._lastSavedAt = new Date()

    if (this._state === 'saving') {
      this._state = 'editing'
    }

    this._recalculateDirty()
    this._notify()
  }

  get isRootDeletionPending(): boolean {
    return this._rootDeletionPending
  }

  /** Pause saves without discarding edits until the deletion outcome is known. */
  pauseForRootDeletion(): { ready: Promise<void>, resume: () => void } {
    this._rootDeletionPending = true
    this._cancelAutoSaveTimer()

    return {
      ready: this._pendingSave || Promise.resolve(),
      resume: () => {
        this._rootDeletionPending = false
        if (!this._destroyed && this._autoSave && this._dirty) {
          this._scheduleAutoSave()
        }
      },
    }
  }

  /** End this context without saving edits when its root is being deleted. */
  async deactivateForRootDeletion(): Promise<void> {
    this._rootDeletionPending = false
    const pendingSave = this._pendingSave
    this.cancelEditing()
    this.destroy()

    // An already-started save must finish before deletion, otherwise it could
    // recreate the deleted document after the delete request completes.
    if (pendingSave) await pendingSave
  }

  /** Cleanup. Resolves pending edits and removes all callbacks. */
  destroy() {
    if (this._destroyed || this._rootDeletionPending) return

    this._resolveDirtyDocumentsBeforeDetach()
    this._destroyed = true

    this._cancelAutoSaveTimer()

    this._tracked.forEach(entry => {
      entry.document.setMutationCallback(null)
    })

    this._tracked.clear()
    this._deletedDocuments.clear()
    this._listeners.clear()
    this._flushCallback = null
    this._discardCallback = null
    this._saveChangesConfirmation = null
  }

  // --- Private ---

  private _onDocumentMutated(documentId: string, { notify = false } = {}) {
    const wasDirty = this._dirty
    const entry = this._tracked.get(documentId)

    // Re-evaluate dirty from all tracked documents' field-level state
    this._recalculateDirty()

    logEditContext('[EditContext] document mutated', {
      context: this._debugName,
      documentId,
      documentDirty: entry?.document?.isDirty,
      dirtyFieldCount: (entry?.document as any)?.dirtyFieldCount,
      wasDirty,
      dirty: this._dirty,
      state: this._state,
      autoSave: this._autoSave,
      autoSaveDelay: this._autoSaveDelay,
      trackedDocumentIds: this.trackedDocumentIds,
    })

    // Manual-save modes enter editing as soon as a real change is recorded.
    if ((this._implicit || this._explicit) && this._state === 'idle' && this._dirty) {
      this._state = 'editing'
    }

    // AutoSave: schedule a batched save
    if (this._autoSave && this._state === 'editing' && this._dirty) {
      logEditContext('[EditContext] schedule autosave', {
        context: this._debugName,
        documentId,
        autoSaveDelay: this._autoSaveDelay,
      })
      this._scheduleAutoSave()
    }

    if (notify || wasDirty !== this._dirty || this._state === 'editing') {
      this._notify()
    }
  }

  private _recalculateDirty() {
    let dirty = false

    this._tracked.forEach(entry => {
      if (entry.document.isDirty || entry.options.newDocument) {
        dirty = true
      }
    })

    this._dirty = dirty || this._deletedDocuments.size > 0
  }

  private _scheduleAutoSave() {
    if (this._rootDeletionPending) {
      return
    }

    // Debounce: reset the timer on every mutation.
    // Only save after N seconds of silence (no new mutations).
    if (this._autoSaveTimer !== null) {
      clearTimeout(this._autoSaveTimer)
    }

    this._autoSaveTimer = setTimeout(() => {
      this._autoSaveTimer = null

      if (this._destroyed || !this._dirty) return

      this._flush()
    }, this._autoSaveDelay)
  }

  private _cancelAutoSaveTimer() {
    if (this._autoSaveTimer !== null) {
      clearTimeout(this._autoSaveTimer)
      this._autoSaveTimer = null
    }
  }

  private _getDirtyDocuments(): EditableDocument[] {
    const dirtyDocuments: EditableDocument[] = []

    this._tracked.forEach(entry => {
      if (entry.document.isDirty || entry.options.newDocument) {
        dirtyDocuments.push(entry.document)
      }
    })

    return dirtyDocuments
  }

  private _getDocumentsWithDirtyFields(): EditableDocument[] {
    const dirtyDocuments: EditableDocument[] = []

    this._tracked.forEach(entry => {
      const document = entry.document as any
      const dirtyFieldCount = document.dirtyFieldCount ?? 0

      if (dirtyFieldCount > 0) {
        dirtyDocuments.push(entry.document)
      }
    })

    return dirtyDocuments
  }

  private _resolveDirtyDocumentsBeforeDetach() {
    if (!this._dirty) return
    // A save on its way finishes on its own: asking now would offer to save
    // what is being saved (a dialog closed by its own Save, say).
    if (this._pendingSave || this._state === 'saving') return

    this._cancelAutoSaveTimer()

    if (this._autoSave) {
      this._flush()
      return
    }

    const dirtyDocuments = this._getDocumentsWithDirtyFields()
    // Only documents with unsaved edits are asked about: a new document that
    // was saved meanwhile, or never touched, is not.
    const documentsToConfirm = dirtyDocuments.length > 0 ? dirtyDocuments : this.trackedDocuments.filter(document => document.isDirty)

    if (documentsToConfirm.length === 0 && this._deletedDocuments.size === 0) {
      this._state = 'idle'
      this._dirty = false
      this._notify()
      return
    }

    if (this._confirmSaveChanges(documentsToConfirm)) {
      this._flush()
      return
    }

    dirtyDocuments.forEach(document => {
      if (document.revertToBase) {
        document.revertToBase()
      }
    })

    this._state = 'idle'
    this._dirty = false
    this._deletedDocuments.clear()
    this._notify()
  }

  private _confirmSaveChanges(documents: EditableDocument[]): boolean {
    if (this._saveChangesConfirmation) {
      return this._saveChangesConfirmation(documents)
    }

    const confirmFn = (typeof globalThis !== 'undefined' && typeof (globalThis as any).confirm === 'function')
      ? (globalThis as any).confirm
      : null

    if (!confirmFn) return false

    const count = documents.length
    const documentLabel = count === 1 ? 'document has' : 'documents have'
    return confirmFn(`${count} edited ${documentLabel} unsaved changes. Save the changes now?`)
  }

  private _flush() {
    if (!this._dirty || this._pendingSave || this._rootDeletionPending) return

    const dirtyDocuments = this._getDirtyDocuments()
    const deletedDocuments = this.deletedDocuments

    if (dirtyDocuments.length === 0 && deletedDocuments.length === 0) return

    this._state = 'saving'
    this._notify()

    logEditContext('[EditContext] flush dirty documents', {
      context: this._debugName,
      documentIds: dirtyDocuments.map(document => document.id),
      deletedDocumentIds: deletedDocuments.map(document => document.id),
    })

    let save: void | Promise<void>
    try {
      save = this._flushCallback?.(dirtyDocuments, deletedDocuments)
    } catch (error) {
      save = Promise.reject(error)
    }

    if (!save) this._acknowledgeDeletions(deletedDocuments)

    this._pendingSave = Promise.resolve(save).then(() => {
      this._acknowledgeDeletions(deletedDocuments)
      this._markCreatedDocumentsSaved(dirtyDocuments)
      this.onSaveCompleted()
    }).catch(error => {
      this._state = 'editing'
      this._recalculateDirty()
      this._notify()
      throw error
    }).finally(() => {
      this._pendingSave = null
      if (this._autoSave && this._dirty && !this._destroyed) this._scheduleAutoSave()
    })
    // Autosave has no awaiting caller; navigation still awaits the original promise.
    void this._pendingSave.catch(error => console.error('EditContext save failed', error))
  }

  private _acknowledgeDeletions(documents: Types.Document[]) {
    for (const document of documents) {
      if (this._deletedDocuments.get(document.id) === document) {
        this._deletedDocuments.delete(document.id)
      }
    }
  }

  private _markCreatedDocumentsSaved(documents: EditableDocument[]) {
    for (const document of documents) {
      const entry = this._tracked.get(document.id)
      if (entry) entry.options.newDocument = false
    }
  }
}

/**
 * Handle exposed to dynamic component controllers (designs).
 * Available in init() for track(); resolves to a real EditContext on mount().
 * Mirrors the SharedContextApiForDynamicComponent pattern.
 */
export class EditContextHandle {
  private _context: EditContext | null = null
  private _el: HTMLElement | null = null
  private _pendingTracks: { document: EditableDocument, options: EditContextTrackOptions }[] = []
  private _released = false
  private _ownsContext = false
  private _registry: EditContextRegistry
  private _options: EditContextCreateOptions
  private _listeners: Set<() => void> = new Set()
  private _listenerUnsubscribers: Map<() => void, () => void> = new Map()

  constructor(registry: EditContextRegistry, options: EditContextCreateOptions = {}) {
    this._registry = registry
    this._options = options
  }

  get context(): EditContext | null { return this._context }

  get isEditing(): boolean { return this._context?.isEditing ?? false }
  get isSaving(): boolean { return this._context?.isSaving ?? false }
  get isDirty(): boolean { return this._context?.isDirty ?? false }
  get lastSavedAt(): Date | null { return this._context?.lastSavedAt ?? null }
  get isAutoSave(): boolean { return this._context?.isAutoSave ?? false }
  get isExplicit(): boolean { return this._context?.isExplicit ?? false }
  get isImplicit(): boolean { return this._context?.isImplicit ?? false }
  get ownsContext(): boolean { return this._ownsContext }
  get debugSnapshot() { return this._context?.debugSnapshot() ?? null }

  /**
   * Track a document. If called before mount, queued until mount resolves.
   */
  track(document: EditableDocument, options: EditContextTrackOptions = {}) {
    if (this._released) return

    const ancestorContext = this._registry.findAncestorContextForDocument(document, this._context)

    if (ancestorContext && ancestorContext !== this._context) {
      this._useContext(ancestorContext, false)
    }

    if (this._context) {
      this._context.track(document, options)
    } else {
      this._pendingTracks.push({ document, options })
    }
  }

  startEditing() { this._context?.startEditing() }
  stopEditing() { this._context?.stopEditing() }
  saveOnFieldBlur() { this._context?.saveOnFieldBlur() }
  cancelEditing() { this._context?.cancelEditing() }

  /**
   * Destroy the current context and create a fresh one.
   * Used when switching modes (e.g. autoSave → implicit).
   */
  reset() {
    if (this._released) return

    if (this._context) {
      if (this._ownsContext) {
        this._context.destroy()
        this._registry.remove(this._context)
      }
      this._context = this._registry.create()
      this._ownsContext = true
      this._context.configure(this._options)
      this._wireListenersToCurrentContext()

      if (this._el) {
        ;(this._el as any)[EDIT_CONTEXT_DOM_KEY] = this._context
      }
    }
  }

  onChange(listener: () => void): () => void {
    this._listeners.add(listener)
    if (this._context) {
      this._listenerUnsubscribers.set(listener, this._context.onChange(listener))
    }

    return () => {
      this._listeners.delete(listener)
      const unsubscribe = this._listenerUnsubscribers.get(listener)
      unsubscribe?.()
      this._listenerUnsubscribers.delete(listener)
    }
  }

  /**
   * Called on mount. Resolves pending tracks into a real EditContext.
   */
  mount(el: HTMLElement) {
    if (this._released) return
    if (this._context) return

    this._el = el

    const inheritedContext = this._findExistingContext(el)
    const context = inheritedContext || this._registry.create()
    this._useContext(context, !inheritedContext)

    // Mark the DOM element so child components can find this context
    if (el) {
      if (this._ownsContext) {
        context.configure(this._options)
        context.setDebugName((el as any).dataset?.aeEditContextName || this._options.debugName || el.id || el.className || el.tagName)
      }
      ;(el as any)[EDIT_CONTEXT_DOM_KEY] = context
    }

    // Flush pending tracks
    for (let i = 0; i < this._pendingTracks.length; i++) {
      const pending = this._pendingTracks[i]
      context.track(pending.document, pending.options)
    }
    this._pendingTracks.length = 0
  }

  /**
   * Called on destroy. Flushes autosave, cleans up.
   */
  release() {
    if (this._released) return
    this._released = true

    // Clean up DOM marker
    if (this._el) {
      ;(this._el as any)[EDIT_CONTEXT_DOM_KEY] = null
      this._el = null
    }

    if (this._context) {
      if (this._ownsContext) {
        this._context.destroy()
        this._registry.remove(this._context)
      }
      this._context = null
      this._ownsContext = false
    }

    this._clearListenerWiring()
    this._listeners.clear()
    this._pendingTracks.length = 0
  }

  private _findExistingContext(el: HTMLElement): EditContext | null {
    for (const pending of this._pendingTracks) {
      const context = this._registry.findAncestorContextForDocument(pending.document, null)
      if (context) {
        return context
      }
    }

    return el ? findEditContextInDOM(el) : null
  }

  private _useContext(context: EditContext, ownsContext: boolean) {
    if (this._context === context && this._ownsContext === ownsContext) {
      return
    }

    if (this._context && this._ownsContext) {
      this._context.destroy()
      this._registry.remove(this._context)
    }

    this._context = context
    this._ownsContext = ownsContext

    if (this._el) {
      ;(this._el as any)[EDIT_CONTEXT_DOM_KEY] = context
    }

    this._wireListenersToCurrentContext()
  }

  private _wireListenersToCurrentContext() {
    this._clearListenerWiring()

    if (!this._context) {
      return
    }

    for (const listener of this._listeners) {
      this._listenerUnsubscribers.set(listener, this._context.onChange(listener))
    }
  }

  private _clearListenerWiring() {
    for (const unsubscribe of this._listenerUnsubscribers.values()) {
      unsubscribe()
    }

    this._listenerUnsubscribers.clear()
  }

}

/**
 * Global registry of all active EditContexts.
 * Lives on the root Aeppic instance.
 */
export class EditContextRegistry {
  private _contexts: Set<EditContext> = new Set()
  private _flushCallback: FlushCallback | null = null
  private _discardCallback: ((documents: EditableDocument[]) => void) | null = null
  private _saveChangesConfirmation: SaveChangesConfirmation | null = null
  private _listeners: Set<() => void> = new Set()
  private _contextUnsubscribers: Map<EditContext, () => void> = new Map()
  private _changeToken = 0

  get hasPendingNavigationEdits(): boolean {
    return this.navigationEditContexts.length > 0
  }

  private get navigationEditContexts(): EditContext[] {
    return Array.from(this._contexts).filter(context => !context.isDestroyed && (context.isDirty || context.isSaving))
  }

  /** The renderer owns the three choices; Stay must never mutate any edit context. */
  async confirmNavigation(confirm: (save: () => Promise<void>, discard: () => void) => Promise<boolean>): Promise<boolean> {
    if (!this.hasPendingNavigationEdits) return true

    const autosaveContexts = this.navigationEditContexts.filter(context => context.isAutoSave)
    if (autosaveContexts.length > 0) {
      // Finish independent autosaves before deciding whether a user choice is needed.
      // Failed contexts remain dirty and participate in the Save/Discard dialog.
      await Promise.allSettled(autosaveContexts.map(context => context.saveBeforeNavigation()))
      if (!this.hasPendingNavigationEdits) return true
    }

    return confirm(async () => {
      for (const context of this.navigationEditContexts) {
        await context.saveBeforeNavigation()
      }
    }, () => {
      for (const context of this.navigationEditContexts) {
        context.cancelEditing()
      }
    })
  }

  get changeToken(): number {
    return this._changeToken
  }

  onChange(listener: () => void): () => void {
    this._listeners.add(listener)
    return () => { this._listeners.delete(listener) }
  }

  private _notify() {
    this._changeToken++
    this._listeners.forEach(listener => {
      try { listener() } catch (e) { console.error('EditContextRegistry listener error', e) }
    })
  }

  /**
   * Set the callback that performs actual saves (Aeppic.save).
   */
  setFlushCallback(callback: FlushCallback) {
    this._flushCallback = callback
  }

  setDiscardCallback(callback: ((documents: EditableDocument[]) => void) | null) {
    this._discardCallback = callback
  }

  setSaveChangesConfirmation(callback: SaveChangesConfirmation | null) {
    this._saveChangesConfirmation = callback
    this._contexts.forEach(context => context.setSaveChangesConfirmation(callback))
  }

  discard(documents: EditableDocument[]) {
    this._discardCallback?.(documents)
  }

  /**
   * Create a new EditContext and register it.
   */
  create(): EditContext {
    const context = new EditContext()
    context.setFlushCallback(this._flushCallback)
    context.setDiscardCallback(this._discardCallback)
    context.setSaveChangesConfirmation(this._saveChangesConfirmation)
    this._contexts.add(context)
    this._contextUnsubscribers.set(context, context.onChange(() => this._notify()))
    this._notify()
    return context
  }

  /**
   * Remove a destroyed context from the registry.
   */
  remove(context: EditContext) {
    if (context.isRootDeletionPending) {
      return
    }

    const unsubscribe = this._contextUnsubscribers.get(context)
    unsubscribe?.()
    this._contextUnsubscribers.delete(context)
    this._contexts.delete(context)
    this._notify()
  }

  /**
   * Create a handle for a dynamic component.
   */
  createHandle(options: EditContextCreateOptions = {}): EditContextHandle {
    return new EditContextHandle(this, options)
  }

  /**
   * Find the EditContext tracking a given document ID.
   * Returns null if the document is not tracked by any context.
   */
  findContextForDocument(documentId: string): EditContext | null {
    let found: EditContext | null = null

    this._contexts.forEach(context => {
      if (!found && !context.isDestroyed && context.trackedDocumentIds.indexOf(documentId) >= 0) {
        found = context
      }
    })

    return found
  }

  /** Discard root edits only after deletion succeeds; retain them on failure. */
  async withRootDeletion(documentId: string, deleteRoot: () => Promise<void>): Promise<boolean> {
    const contexts = Array.from(this._contexts).filter(context => {
      if (context.isDestroyed) {
        return false
      }
      const root = context.trackedDocuments[0]
      return root && documentBelongsToTree(root, documentId)
    })

    if (contexts.length === 0) {
      return false
    }

    const pauses = contexts.map(context => context.pauseForRootDeletion())
    try {
      await Promise.all(pauses.map(pause => pause.ready))
      await deleteRoot()
    } catch (error) {
      pauses.forEach(pause => pause.resume())
      throw error
    }

    await Promise.all(contexts.map(context => context.deactivateForRootDeletion()))
    contexts.forEach(context => this.remove(context))
    return true
  }

  async deactivateContextsForRootDeletion(documentId: string): Promise<boolean> {
    const contexts = Array.from(this._contexts).filter(context => {
      if (context.isDestroyed) return false
      const root = context.trackedDocuments[0]
      return root && documentBelongsToTree(root, documentId)
    })

    await Promise.all(contexts.map(context => context.deactivateForRootDeletion()))
    return contexts.length > 0
  }

  findTrackedDocument(documentId: string): EditableDocument | null {
    for (const context of this._contexts) {
      if (context.isDestroyed) continue
      const entry = context.getTrackedEntry(documentId)
      if (entry) return entry.document
    }
    return null
  }

  getDocumentTreeChangeSummary(documentId: string): EditContextDocumentTreeChangeSummary {
    const summary = emptyDocumentTreeChangeSummary()

    if (!documentId) {
      return summary
    }

    this._contexts.forEach(context => {
      if (context.isDestroyed) {
        return
      }

      const contextSummary = context.getDocumentTreeChangeSummary(documentId)
      summary.added += contextSummary.added
      summary.changed += contextSummary.changed
      summary.removed += contextSummary.removed
    })

    summary.hasChanges = summary.added > 0 || summary.changed > 0 || summary.removed > 0
    return summary
  }

  notifyTrackedDocumentChanged(documentId: string) {
    const context = this.findContextForDocument(documentId)
    context?.notifyDocumentChanged(documentId)
  }

  /**
   * Check if a document is tracked by any active EditContext.
   */
  isTracked(documentId: string): boolean {
    return this.findContextForDocument(documentId) !== null
  }

  /**
   * Find an EditContext that tracks any of the given document IDs.
   * Intended for ancestor lookups: pass [parentId, ...ancestorIds].
   * Returns the first match (closest ancestor should come first).
   */
  findContextForAnyOf(documentIds: string[], excludeContext: EditContext | null = null): EditContext | null {
    if (!documentIds || documentIds.length === 0) return null

    for (const id of documentIds) {
      if (!id) continue
      const ctx = this.findContextForDocument(id)
      if (ctx && ctx !== excludeContext) return ctx
    }

    return null
  }

  findAncestorContextForDocument(document: Types.Document, excludeContext: EditContext | null = null): EditContext | null {
    const parentId = document.p
    const ancestors: string[] = Array.isArray(document.a) ? document.a : []
    const lookupIds = parentId ? [parentId, ...ancestors] : ancestors

    return this.findContextForAnyOf(lookupIds, excludeContext)
  }

  debugSnapshot() {
    const snapshots: ReturnType<EditContext['debugSnapshot']>[] = []
    this._contexts.forEach(context => {
      if (!context.isDestroyed) {
        snapshots.push(context.debugSnapshot())
      }
    })
    return snapshots
  }

  private _findDomElementsForContexts(): Map<EditContext, HTMLElement> {
    const map = new Map<EditContext, HTMLElement>()
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT)
    let node: HTMLElement
    while ((node = walker.nextNode() as HTMLElement)) {
      const ctx = (node as any)[EDIT_CONTEXT_DOM_KEY]
      if (ctx && ctx instanceof EditContext && !ctx.isDestroyed) {
        map.set(ctx, node)
      }
    }
    return map
  }

  debugOverlay(mountElement?: HTMLElement) {
    const registry = this
    const container = mountElement || document.body
    const overlay = document.createElement('div')
    overlay.id = '__ae-edit-context-debug'
    Object.assign(overlay.style, {
      position: 'fixed',
      bottom: '8px',
      right: '8px',
      maxWidth: '480px',
      maxHeight: '50vh',
      overflow: 'auto',
      background: '#1a1a2e',
      color: '#e0e0e0',
      fontFamily: 'monospace',
      fontSize: '11px',
      padding: '8px 12px',
      borderRadius: '6px',
      zIndex: '99999',
      boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
      lineHeight: '1.5',
    })

    let highlightEl: HTMLElement | null = null

    const clearHighlight = () => {
      if (highlightEl) {
        highlightEl.style.outline = highlightEl.dataset.__aeDebugPrevOutline || ''
        highlightEl.style.outlineOffset = highlightEl.dataset.__aeDebugPrevOffset || ''
        delete highlightEl.dataset.__aeDebugPrevOutline
        delete highlightEl.dataset.__aeDebugPrevOffset
        highlightEl = null
      }
    }

    const showHighlight = (el: HTMLElement, color: string) => {
      clearHighlight()
      highlightEl = el
      el.dataset.__aeDebugPrevOutline = el.style.outline
      el.dataset.__aeDebugPrevOffset = el.style.outlineOffset
      el.style.outline = `3px solid ${color}`
      el.style.outlineOffset = '2px'
    }

    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

    // Keep a stable reference to snapshots so hover handlers survive re-renders
    let currentEntries: { snap: ReturnType<EditContext['debugSnapshot']>, el: HTMLElement | null }[] = []
    let lastJson = ''
    let hoveredIdx = -1

    const buildHtml = () => {
      let html = `<div style="font-size:12px;font-weight:bold;margin-bottom:6px;color:#7eb8da;cursor:default">EditContext Debug <span style="color:#888">(${currentEntries.length})</span></div>`

      if (currentEntries.length === 0) {
        html += '<div style="color:#888">No active contexts</div>'
      }

      currentEntries.forEach(({ snap: ctx, el }, i) => {
        const color = COLORS[i % COLORS.length]
        const stateColor = ctx.state === 'editing' ? '#4ade80' : ctx.state === 'saving' ? '#facc15' : '#888'
        const dirtyBadge = ctx.dirty ? '<span style="color:#f87171;margin-left:4px">DIRTY</span>' : ''
        const elTag = el ? `<${el.tagName.toLowerCase()}${el.id ? '#' + el.id : ''}>` : '(no DOM)'

        html += `<div data-ctx-idx="${i}" style="border-top:1px solid #333;padding:4px 0;margin-top:4px;cursor:pointer;border-left:3px solid ${color};padding-left:8px">`
        html += `<div style="color:${color};font-weight:bold">${ctx.name}</div>`
        html += `<div><span style="color:${stateColor}">[${ctx.state}]</span>${dirtyBadge} <span style="color:#888;font-size:10px">auto:${ctx.autoSave} impl:${ctx.implicit} expl:${ctx.explicit}</span></div>`
        html += `<div style="color:#888;font-size:10px">${elTag}</div>`

        if (ctx.documents.length === 0) {
          html += `<div style="padding-left:10px;color:#666;font-style:italic">no tracked documents</div>`
        }

        for (const doc of ctx.documents) {
          const docDirty = doc.dirty ? ` <span style="color:#f87171">dirty(${doc.dirtyFieldCount})</span>` : ' <span style="color:#4ade80">clean</span>'
          const label = doc.text ? `${doc.text}` : doc.formId || ''
          const role = doc.root
            ? '<span style="color:#facc15;font-weight:bold">ROOT</span> '
            : '<span style="color:#888">DESC</span> '
          html += `<div style="padding-left:10px;color:#d4d4d4">`
          html += `${role}<span style="color:#93c5fd">${doc.id.substring(0, 12)}…</span>${docDirty}`
          if (label) html += ` <span style="color:#a78bfa">${label}</span>`
          html += `</div>`
        }

        html += '</div>'
      })

      return html
    }

    const render = () => {
      const domMap = registry._findDomElementsForContexts()
      const entries: typeof currentEntries = []

      registry._contexts.forEach(ctx => {
        if (!ctx.isDestroyed) {
          entries.push({ snap: ctx.debugSnapshot(), el: domMap.get(ctx) || null })
        }
      })

      const json = JSON.stringify(entries.map(e => e.snap))
      if (json === lastJson) return

      lastJson = json
      currentEntries = entries
      overlay.innerHTML = buildHtml()
    }

    // Use mouseover/mouseout (they bubble, unlike mouseenter/mouseleave)
    overlay.addEventListener('mouseover', (e) => {
      const row = (e.target as HTMLElement).closest('[data-ctx-idx]') as HTMLElement
      if (!row) return
      const idx = parseInt(row.dataset.ctxIdx)
      if (idx === hoveredIdx) return
      hoveredIdx = idx
      const entry = currentEntries[idx]
      if (entry?.el) showHighlight(entry.el, COLORS[idx % COLORS.length])
    })

    overlay.addEventListener('mouseout', (e) => {
      const related = (e as MouseEvent).relatedTarget as HTMLElement
      if (related && overlay.contains(related)) return
      hoveredIdx = -1
      clearHighlight()
    })

    render()
    const interval = setInterval(render, 1000)

    overlay.addEventListener('dblclick', (e) => {
      if ((e.target as HTMLElement).closest('[data-ctx-idx]')) return
      clearHighlight()
      clearInterval(interval)
      overlay.remove()
    })

    container.appendChild(overlay)
    return { stop: () => { clearHighlight(); clearInterval(interval); overlay.remove() } }
  }

  destroy() {
    this._contexts.forEach(context => context.destroy())
    this._contexts.clear()
    this._flushCallback = null
  }
}
